import React, { useState, useCallback } from 'react';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import CommonBubble from '@home/components/CommonBubble';
import { useContentExporter } from '@/pages/aigc/hooks/useContentExporter';
import imageIcon from '@/pages/dashboard/assets/svg/image-icon.svg';
import type { IImageDownloadButtonProps } from './type';
import '../index.scss';

/**
 * 图片下载按钮（dashboard 通用插件）。
 *
 * 设计特点：
 * - 底层调用 aigc/hooks/useContentExporter 的 exportPng（公共 hook）
 * - 内置「防重入」：导出过程中按钮自动禁用，避免并发调用把 DOM 临时样式留死
 * - 内置 i18n 文案（common_download_image）与 loading 蒙层
 * - 按钮自身打 data-export-hidden，不会被截入图片
 *
 * 使用示例：
 * ```tsx
 * const cardRef = useRef<HTMLDivElement>(null);
 * <div ref={cardRef}>
 *   <ImageDownloadButton
 *     targetRef={cardRef}
 *     filename="非简中重点国家表现"
 *     exportOptions={{
 *       expandSelectors: ['.db-alitable-wrapper', '.art-table-body'],
 *       onBeforeCapture: (root) => { ... return cleanup; },
 *     }}
 *   />
 * </div>
 * ```
 */
export const ImageDownloadButton: React.FC<IImageDownloadButtonProps> = (props) => {
  const {
    targetRef,
    filename = 'chart',
    exportOptions,
    className,
    style,
    tipText,
    disabled,
    onClick,
  } = props;
  const { formatMessage } = useLocal();
  const { exportPng } = useContentExporter();
  const [isExporting, setIsExporting] = useState(false);

  const handleClick = useCallback(async () => {
    if (!targetRef.current || disabled || isExporting) {
      return;
    }
    setIsExporting(true);
    try {
      try {
        onClick?.();
      } catch (error) {
        console.warn('[ImageDownloadButton] onClick 执行失败:', error);
      }
      await exportPng(targetRef.current, {
        ...exportOptions,
        filename: `${filename || 'chart'}.png`,
      });
    } catch (error) {
      console.error('[ImageDownloadButton] 导出图片失败:', error);
    } finally {
      setIsExporting(false);
    }
  }, [targetRef, disabled, isExporting, onClick, exportPng, exportOptions, filename]);

  const inactive = Boolean(disabled) || isExporting;

  return (
    <div
      className={className}
      style={{
        ...style,
        ...(inactive
          ? { opacity: 0.45, cursor: 'not-allowed', pointerEvents: 'none' as const }
          : undefined),
      }}
    >
      <CommonBubble
        dark
        placement="top"
        placementOffset={10}
        content={tipText || formatMessage({ id: 'common_download_image' })}
      >
        <div
          className="dbd-image-download-btn"
          data-export-hidden="true"
          onClick={() => { void handleClick() }}
        >
          {/* 用 mask-image 把 SVG 当蒙版渲染：颜色由 background-color 控制，
              hover 时切到主蓝色。比 <img> 灵活、且能跟 DownloadButton 的 SvgIcon 行为一致。 */}
          <span
            className="dbd-image-download-btn__icon"
            style={{
              WebkitMaskImage: `url(${imageIcon})`,
              maskImage: `url(${imageIcon})`,
            }}
          />
        </div>
      </CommonBubble>
    </div>
  );
};

export default ImageDownloadButton;
